// src/components/AI/AiAssistantWpPublicChatPanel.jsx
import React from 'react'
import PropTypes from 'prop-types'
import { Form, Input, Button } from 'antd'
import { CopyOutlined } from '@ant-design/icons'

import { createUserToken } from '../../../../config/utils'
import { useParams } from 'react-router-dom'
import { useStores } from '../../../../cls/stores/MobXProvider'
import { DATE_TIME_FORMAT } from '../../../../config'
import { FormatedTimestamp } from '../../../../components/Shared'

/**
 * Support for WordPress public chat panel in AI Assistant.
 * This component allows users to configure their WordPress site URL and OpenAI API key.
 * It also checks the subscription status and provides feedback on saving settings.
 * fields tracked: - ai_wp_chat_site_url, ai_wp_chat_openai_api_key, ai_wp_chat_subscription_id
 */
export default function AiAssistantWpPublicChatPanel({ form, disabled }) {
	const { itmId } = useParams()
	const { users } = useStores()
	const { loggedInUser } = users
	const user = users.requireUserById(itmId)
	const userToken = loggedInUser.aiWpChat?.token
	console.log('userToken', userToken)

	const validateOpenAIKey = async () => {
		try {
			// Trigger validation for the OpenAI API key field
			await form.validateFields(['ai_wp_chat_openai_api_key'])
			// If validation passes, we can make an actuall OpenAi API call here to verify the key
			let useToken = userToken
			if (!userToken) {
				useToken = createUserToken()
				// user.setAiWpChatToken(_newUserToken)
			}
			// Make a POST request to validate the OpenAI key with the backend API
			const response = await fetch(
				'https://platform.webackit.com/ai-wp-public-chat/v1/validate-ai-key',
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json'
					},
					body: JSON.stringify({
						tenant_id: user.user_id,
						site_url: form.getFieldValue('ai_wp_chat_site_url'),
						api_key: form.getFieldValue(
							'ai_wp_chat_openai_api_key'
						),
						api_token: useToken
					})
				}
			)
			let data
			try {
				data = await response.json()
			} catch (e) {
				data = { ok: false, error: 'Invalid server response' }
			}
			if (data.ok) {
				// If validation is successful, show success message
				alert(
					'OpenAI key validated, we will execute a save to make sure we preserve the data.'
				)

				// We save only the first 6 characters of the key for display purposes
				// The full key is stored securely on the server side
				// Example: sk-123456abcdefg -> sk-123456
				const secureOpenAiKey = (key) => {
					if (typeof key !== 'string' || !key.startsWith('sk-')) {
						return ''
					}
					return key.slice(0, 8) + '...' // Show only first 8 characters
				}

				const validatedFor = (data) => {
					// return construct of data.token + ai_wp_chat_site_url
					return `${data.api_token}@${form.getFieldValue(
						'ai_wp_chat_site_url'
					)}`
				}

				form.setFieldsValue({
					ai_wp_chat_openai_api_key: secureOpenAiKey(
						form.getFieldValue('ai_wp_chat_openai_api_key')
					),
					ai_wp_chat_api_token: data.api_token,
					ai_wp_chat_openai_validated_at: data.openai_validated_at,
					ai_wp_chat_openai_validated_for: validatedFor(data)
				})
				const _doSave = () => {
					form.submit()
				}
				setTimeout(_doSave, 100) // slight delay to ensure form values are updated before submit
			} else {
				// If validation fails, show error message from the response
				alert(`Validation failed: ${data.error || 'Unknown error'}`)
			}
		} catch (error) {
			// If validation fails, show error message
			alert('Please enter a valid OpenAI key.')
		}
	}

	const watch_ai_wp_chat_openai_api_key = Form.useWatch(
		['ai_wp_chat_openai_api_key'],
		form
	)

	const watch_ai_wp_chat_openai_validated_at = Form.useWatch(
		['ai_wp_chat_openai_validated_at'],
		form
	)
	const watch_ai_wp_chat_api_token = Form.useWatch(
		['ai_wp_chat_api_token'],
		form
	)

	const watch_ai_wp_chat_openai_validated_for = Form.useWatch(
		['ai_wp_chat_openai_validated_for'],
		form
	)

	const curentTokenAndWebsiteIsValidated = () => {
		// check if the current token and website url combination has been validated
		const currentToken = form.getFieldValue('ai_wp_chat_api_token')
		const currentWebsite = form.getFieldValue('ai_wp_chat_site_url')
		const expectedValidatedFor = `${currentToken}@${currentWebsite}`
		return (
			currentToken &&
			currentWebsite &&
			currentToken === userToken &&
			expectedValidatedFor === watch_ai_wp_chat_openai_validated_for
		)
	}

	const curentOpenAiKeyIsDefinedAndFull = () => {
		// using watch_ai_wp_chat_openai_api_key as reference, check if it's defined and it's lenght is not only 8 chars
		return (
			typeof watch_ai_wp_chat_openai_api_key === 'string' &&
			watch_ai_wp_chat_openai_api_key.length > 8
		)
	}
	console.log(
		'watch_ai_wp_chat_openai_validated_at',
		watch_ai_wp_chat_openai_validated_at
	)

	return (
		<div>
			<Form.Item
				label="Website URL"
				name="ai_wp_chat_site_url"
				rules={[
					{
						message: 'Please provide your website URL'
					},
					{
						type: 'url',
						message: 'Please enter a valid URL (https://...)'
					}
				]}
			>
				<Input
					placeholder="https://example.com"
					onChange={() => {
						const tokenAndWebsiteValid =
							curentTokenAndWebsiteIsValidated()
						if (
							!tokenAndWebsiteValid &&
							watch_ai_wp_chat_openai_validated_at
						) {
							alert(
								'Curent url no longer matches to previous validation. Changing the website URL requires re-validating the OpenAI key.'
							)
						}
					}}
				/>
			</Form.Item>

			<Form.Item
				label="OpenAI API Key"
				name="ai_wp_chat_openai_api_key"
				tooltip="Paste your OpenAI key here. It's encrypted at rest and only used by the API when generating replies."
				rules={[
					{ required: false },
					() => ({
						validator(_, value) {
							if (!value) return Promise.resolve()
							if (/^sk-/.test(value)) return Promise.resolve()
							return Promise.reject(
								new Error(
									'This does not look like an OpenAI key (should start with sk-).'
								)
							)
						}
					})
				]}
			>
				<Input.Password
					placeholder="sk-..."
					autoComplete="new-password"
				/>
			</Form.Item>

			<Form.Item label="Click to validate your OpenAI key">
				<>
					<Button
						type="primary"
						onClick={validateOpenAIKey}
						disabled={
							(curentOpenAiKeyIsDefinedAndFull()
								? false
								: true) && disabled
						}
					>
						{watch_ai_wp_chat_openai_validated_at
							? 'Re-validate OpenAI Key'
							: 'Validate OpenAI Key'}
					</Button>
					{!curentOpenAiKeyIsDefinedAndFull() && (
						<p style={{ color: 'red' }}>
							OpenAi api key is not defined or not a full. If you
							previously validated it, and you want to validate
							again, you need to add the OpenAi api key again.
						</p>
					)}
				</>
			</Form.Item>

			<Form.Item
				label="Token and OpenAi last validated at"
				name="ai_wp_chat_openai_validated_at"
				style={
					watch_ai_wp_chat_openai_validated_at
						? {}
						: { display: 'none' }
				}
				className="no-input-height no-input-align-help"
				help={
					<FormatedTimestamp
						utcTime={watch_ai_wp_chat_openai_validated_at}
						formatedAs={DATE_TIME_FORMAT}
					/>
				}
			>
				<Input type="hidden" />
			</Form.Item>
			<Form.Item
				label="User ID"
				className="no-input-height no-input-align-help"
				help={
					<Button
						icon={<CopyOutlined />}
						onClick={() => {
							// copy to clipboard
							navigator.clipboard.writeText(String(itmId).trim())
							alert('Token copied to clipboard')
						}}
					>
						{itmId}
					</Button>
				}
			></Form.Item>
			<Form.Item
				label="API Token"
				name="ai_wp_chat_api_token"
				style={watch_ai_wp_chat_api_token ? {} : { display: 'none' }}
				tooltip="This token is linked to your Website URL and is used to authenticate requests from your WordPress site. Active only with subscription."
				className="no-input-height no-input-align-help"
				help={
					<Button
						icon={<CopyOutlined />}
						onClick={() => {
							// copy to clipboard
							navigator.clipboard.writeText(
								String(watch_ai_wp_chat_api_token).trim()
							)
							alert('Token copied to clipboard')
						}}
					>
						{watch_ai_wp_chat_api_token}
					</Button>
				}
			>
				<Input type="hidden" />
			</Form.Item>

			{/* Hidden field to store subscription ID */}
			<Form.Item
				name="ai_wp_chat_subscription_id"
				style={{ display: 'none' }}
			>
				<Input type="hidden" />
			</Form.Item>
		</div>
	)
}

AiAssistantWpPublicChatPanel.propTypes = {
	form: PropTypes.object.isRequired,
	disabled: PropTypes.bool
}
